import React from 'react';
import {useSelector} from 'react-redux';
import {useHistory, useLocation} from 'react-router-dom';
import {FieldArray, useFormikContext} from 'formik';

import {Store, Course} from 'src/types/store';
import {newCourseObject} from './utils';

import Button from 'src/components/button';
import TextButton from 'src/components/TextButton';
import SchoolHeader from './school-header';
import {CourseForm} from './course-form-wrapper';
import InlineCourseForm from './inline-course-form';
import styles from './editing-school-section.module.scss';

interface Props {
  institutionId: string;
  courseGroups: Record<string, Course[]>;
  year: string;
}

const EditingSchoolSection = ({institutionId, courseGroups, year}: Props) => {
  const history = useHistory();
  const {pathname} = useLocation();
  const institution = useSelector((state: Store) => state.institutions[institutionId]);
  const formik = useFormikContext<Record<string, CourseForm[]>>();
  const coursesByGradeSettings = Object.keys(courseGroups);

  return (
    <div className={styles.editing}>
      <SchoolHeader>
        {institution.name}
        <TextButton
          className="ml-16"
          margin={false}
          onClick={() =>
            history.replace({
              pathname: `${pathname}/school/${institutionId}`,
              state: {skipScrolling: true},
            })
          }
          testid="bulk-entry-edit-school"
        >
          Edit
        </TextButton>
      </SchoolHeader>

      {coursesByGradeSettings.map((gradeSettingsStr, index) => {
        const {institutionId: _institutionId, ...gradeSettings} = JSON.parse(gradeSettingsStr);

        return (
          formik.values[gradeSettingsStr] && (
            <FieldArray key={gradeSettingsStr} name={gradeSettingsStr}>
              {(fieldArrayHelpers) => (
                <>
                  {formik.values[gradeSettingsStr].map((course, i) => (
                    <InlineCourseForm
                      key={course._id}
                      hideLabels={i !== 0}
                      prefix={`${gradeSettingsStr}.${i}.`}
                      removeCourse={() => {
                        fieldArrayHelpers.remove(i);
                      }}
                    />
                  ))}

                  <Button
                    outline
                    onClick={() =>
                      fieldArrayHelpers.push({
                        ...newCourseObject({
                          ...institution,
                          gradeSettings,
                        }),
                        schoolYear: year,
                      })
                    }
                    margin={false}
                    className="mt-12"
                  >
                    + Add Course
                  </Button>

                  {index < coursesByGradeSettings.length - 1 && <hr className={styles.divider} />}
                </>
              )}
            </FieldArray>
          )
        );
      })}

      <TextButton
        className="ml-16"
        onClick={() =>
          history.replace({
            pathname: `${pathname}/school/${institutionId}`,
            state: {skipScrolling: true},
          })
        }
      >
        Change default grading period
      </TextButton>
    </div>
  );
};

export default EditingSchoolSection;
